import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getAppointmentsViewData } from 'state/actions/appointments';
import useAgencyFanOutQueries from './useAgencyFanOutQueries';
import {
  agencyRecommendationsKeys,
  carrierMissingAppointmentsRequest,
  hasCarrierMissingEndpoint,
  mergePartialMeta,
  readPartialMeta
} from './api/agencyRecommendations';

/**
 * folds the per-agency responses into the single figure set the featured card shows.
 * `partial` is an OR across agencies and `skippedAgents` a sum, so the notice
 * reflects every agency whose agent list came back incomplete.
 * @param results - fan-out results, one { agencyId, agencyName, data } per agency
 * @param visibleAgencyIds - Set narrowing the card to the agency filter, or null
 */
export function mergeFeaturedCarrierResults(results, visibleAgencyIds) {
  const visible = (results ?? []).filter(
    r => r.data && (!visibleAgencyIds || visibleAgencyIds.has(r.agencyId))
  );
  if (visible.length === 0) return null;

  const { partial, skippedAgents } = mergePartialMeta(
    visible.map(r => readPartialMeta(r.data))
  );

  return {
    carrier: visible.find(r => r.data?.carrier)?.data.carrier ?? '',
    totalCommissions: visible.reduce(
      (sum, r) => sum + (Number(r.data?.totalCommissions) || 0),
      0
    ),
    agencyCount: visible.length,
    agents: visible.flatMap(({ agencyId, agencyName, data }) =>
      (Array.isArray(data?.agents) ? data.agents : []).map(agent => ({
        ...agent,
        agencyId,
        agencyName
      }))
    ),
    partial,
    skippedAgents
  };
}

/**
 * loads the agents missing an appointment with the carrier Zoho currently flags as
 * highlighted. the carrier itself comes from the appointments slice, not from an
 * endpoint of its own.
 * @param agencies - list of { agencyId, agencyName } currently applied
 */
export default function useFeaturedCarrierMissingAppointments(agencies) {
  const dispatch = useDispatch();
  const carriersArray = useSelector(state => state.appointments.carriersArray);
  const carriersFetchTriggeredRef = useRef(false);

  const highlightedCarrier = useMemo(() => {
    if (!Array.isArray(carriersArray) || carriersArray.length === 0) {
      return null;
    }
    return carriersArray.find(c => c?.highlightCarrier) ?? null;
  }, [carriersArray]);

  useEffect(() => {
    if (carriersFetchTriggeredRef.current) return;
    if (Array.isArray(carriersArray) && carriersArray.length > 0) return;
    carriersFetchTriggeredRef.current = true;
    dispatch(getAppointmentsViewData(() => {}, {}, false));
  }, [carriersArray, dispatch]);

  const carrierId = highlightedCarrier?.id;

  const buildEntry = useCallback(
    ({ agencyId }) => ({
      queryKey: agencyRecommendationsKeys.carrierMissing(agencyId, carrierId),
      request: carrierMissingAppointmentsRequest(agencyId, carrierId)
    }),
    [carrierId]
  );

  // the carrier arrives one render after the agencies, so hold the fan-out until
  // there is a carrier to ask about instead of firing a request without one
  const fan = useAgencyFanOutQueries(agencies, buildEntry, {
    enabled: Boolean(carrierId) && hasCarrierMissingEndpoint()
  });

  return {
    loading: fan.isLoading,
    results: fan.results,
    highlightedCarrier,
    failedAgencies: fan.failedAgencies,
    retryFailed: fan.retryFailed
  };
}
